import { CrewUi } from 'corso-types';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { capitalize, identity } from './compute';
import { ClaimType, ExchangeMethod, ResolutionMethod } from './constants';

export const {
  orderAddressSchema,
  shipmentCreateSchema,
  shipmentMethodSchema,
  shipmentRateSchema,
  claimLineItemCreateSchema,
  calculatedOrderLineItemSchema,
  gspOrderSchema,
  shippingClaimSchema,
  settingsSchema,
  registrationSchema,
  locationAddressSchema,
  orderEditActionSchema,
  orderEditCancelOrderActionSchema,
  orderEditUpdateAddressActionSchema,
  orderEditStagedSchema,
  orderEditStagedIdSchema,
  embeddedMessageSchema,
} = CrewUi;

export const typeGuardFromSchema =
  <Output, Def extends z.ZodTypeDef, Input>(
    schema: z.ZodType<Output, Def, Input>,
  ) =>
  (data: unknown): data is Input =>
    schema.safeParse(data).success;

export const mediaAssetSchema = z.object({
  /** The URL to access the file on the CDN. */
  src: z.string(),
  /** The original filename. */
  name: z.string(),
  /** The MIME type of the file. */
  type: z.string(),
  /** The filename as uploaded to the CDN. */
  cdnName: z.string(),
});

export const stringToJSONSchema = z.string().transform((str, ctx) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
    return z.NEVER;
  }
});

export const claimAddressSchema = CrewUi.claimAddressSchema.extend({
  phone: z
    .nullable(
      z
        .string({ message: 'Phone number is required.' })
        .min(1, { message: 'Phone number is required.' }),
    )
    .optional(),
});

export const addressFormSchema = z.object({
  firstName: zfd.text(z.string({ message: 'First name is required.' })),
  lastName: zfd.text(z.string({ message: 'Last name is required.' })),
  line1: zfd.text(z.string({ message: 'Address is required.' })),
  line2: zfd.text(z.string().optional()),
  city: zfd.text(z.string({ message: 'City is required.' })),
  stateOrProvinceCode: zfd.text(
    claimAddressSchema.shape.stateOrProvinceCode.optional(),
  ),
  postalCode: zfd.text(z.string({ message: 'Postal Code is required.' })),
  countryCode: zfd.text(claimAddressSchema.shape.countryCode),
  phone: zfd.text(claimAddressSchema.shape.phone),
});

export const crewOrderLineItemSchema = CrewUi.crewOrderLineItemSchema.extend({
  remainingReturnableQuantity: z.number(),
});

export const exchangeVariantSchema = CrewUi.variantExchangeOptionsSchema;

export const advancedExchangeMethodSchema = CrewUi.AdvancedExchangeMethodSchema;

export type AdvancedExchangeMethod = z.infer<
  typeof advancedExchangeMethodSchema
>;

const customFields = z.record(
  z.object({
    value: z.string(),
    type: z.union([
      CrewUi.customFieldSelectSchema.shape.valueType,
      CrewUi.customFieldGeneralSchema.shape.valueType,
    ]),
  }),
);

/*
 * DraftClaimLineItem schema does two things over the original CrewUi claimLineItemCreateSchema:
 * makes certain fields optional as they have not been selected yet
 * and provides additional fields for easy UI consumption.
 * */
export const draftClaimLineItemSchema = CrewUi.claimLineItemCreateSchema
  .omit({
    // using assets schema instead of images for the UI
    images: true,
    // using a different schema for the UI
    customFields: true,

    // use variant instead of variantExchangeLineItem because need the product information
    variantExchangeLineItem: true,
  })
  .extend({
    id: z.string(),
    lineItem: crewOrderLineItemSchema,
    variant: exchangeVariantSchema.optional(),
    customFields,
    grams: z.number(),
    assets: z.array(mediaAssetSchema).optional(),
    requestedResolutionMethodEnum:
      CrewUi.claimLineItemCreateSchema.shape.requestedResolutionMethodEnum.optional(),
    reasonId: CrewUi.claimLineItemCreateSchema.shape.reasonId.optional(),
    reasonCategoryCode:
      CrewUi.claimLineItemCreateSchema.shape.reasonCategoryCode.optional(),
    shopNow: z.boolean().optional(),
  });

export const variantExchangeClaimSchema = draftClaimLineItemSchema
  .omit({ requestedResolutionMethodEnum: true })
  .extend({
    requestedResolutionMethodEnum: z.literal(ResolutionMethod.variantExchange),
  })
  .required({
    variant: true,
    advancedExchangeMethod: true,
    reasonId: true,
    reasonCategoryCode: true,
  });

export const refundClaimSchema = draftClaimLineItemSchema
  .omit({ requestedResolutionMethodEnum: true })
  .extend({
    requestedResolutionMethodEnum: z.literal(ResolutionMethod.refund),
    variant: z.never().optional(),
    advancedExchangeMethod: z.never().optional(),
  })
  .required({
    reasonId: true,
    reasonCategoryCode: true,
  });

export const giftCardClaimSchema = draftClaimLineItemSchema
  .omit({ requestedResolutionMethodEnum: true })
  .extend({
    requestedResolutionMethodEnum: z.literal(ResolutionMethod.giftCard),
    variant: z.never().optional(),
    advancedExchangeMethod: z.never().optional(),
  })
  .required({
    reasonId: true,
    reasonCategoryCode: true,
  });

export const claimLineItemSchema = draftClaimLineItemSchema.required({
  reasonCategoryCode: true,
  reasonId: true,
  requestedResolutionMethodEnum: true,
});

export const creditResolutionMethodSchema = z.union([
  z.literal(ResolutionMethod.giftCard),
  z.literal(ResolutionMethod.refund),
]);
export type CreditResolutionMethod = z.infer<
  typeof creditResolutionMethodSchema
>;

export const exchangeMethodSchema = z.union([
  z.literal(ExchangeMethod.instant),
  z.literal(ExchangeMethod.standard),
]);

export const crewOrderSchema = CrewUi.orderSchema.extend({
  lineItems: z.array(crewOrderLineItemSchema),
});

const storeSalesChannelSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const draftRegistrationSchema = z.object({
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  storeSalesChannel: storeSalesChannelSchema,
  estimatedPurchaseDate: z.string(),
  address: z.nullable(CrewUi.claimAddressSchema),
  proofOfPurchaseAssets: z.array(mediaAssetSchema).nullable(),
  orderNumber: z.string().optional(),
  referrer: z.string().optional(),
  customFields,
  emailMarketingConsentCollectedOn: z.string().optional(),
});

export const storeParamSchema = z.object({
  store: z
    .string()
    .min(1)
    .refine(
      (store) => {
        // * These are first level paths in the app that should not be used as store names for instance /order/order/:orderId
        // really don't like having this here vs with the router, but it is the central path for the app
        const invalidKeywords = [
          'order',
          'claim',
          'inactive',
          'register',
          'reorder',
        ];

        return !invalidKeywords.includes(store);
      },
      {
        message: 'The store name must not be a reserved keyword.',
      },
    ),
});

/**
 * Schema for the claim type parameter in the URL.
 * It preprocesses the value to ensure it is capitalized.
 * This is useful for parsing claim types from the URL
 * and ensuring they match the ClaimType enum.
 */
export const claimTypeParamSchema = z.preprocess(
  (val) => match(val).with(P.string, capitalize).otherwise(identity),
  z.nativeEnum(ClaimType),
);
